<script lang="ts">
  import Redo from '~icons/mdi/redo';
  import BoardTile from '../board/Tile.svelte';
  import { highlightColors } from '../algorithms/gameLogic';
  import { definitionModal } from '../modals/Definition.svelte';
  import {
    type HighlightColors,
    type Match,
  } from '../types';
  import { page } from '$app/state';
  type Props = {
    word: Pick<Match, 'word' | 'axis' | 'score' | 'swaps'>;
    highlighted?: HighlightColors;
    score?: boolean;
    time?: string;
  };
  const props: Props = $props();

  // Derive game type from URL: /daily and /mini render as daily, /classic
  // (incl. /classic/leaderboard) renders as classic. Twist and other paths
  // fall through to the default highlight/score behavior.
  const path = $derived(page.url.pathname);
  const isDaily = $derived(path.startsWith('/daily') || path.startsWith('/mini'));
  const isClassic = $derived(path.startsWith('/classic'));

  const highlighted = $derived(
    isDaily
      ? props.word.axis === 'col'
        ? 'daily-vertical'
        : 'daily-horizontal'
      : props.highlighted,
  );

  const openDefinition = () => {
    definitionModal.push(props.word.word.map((t) => t.letter).join(''));
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      openDefinition();
    }
  };
</script>

<div
  class="static-word-container"
  onclick={openDefinition}
  onkeypress={handleKeyPress}
  role="button"
  tabindex="-1"
>
  {#if props.time}
    <span class="word-time">{props.time}</span>
  {/if}
  <span class="word">
    {#each props.word.word as tile}
      <BoardTile
        {tile}
        highlighted={highlighted ?? highlightColors[props.word.word.length]}
        size="small"
      />
    {/each}
  </span>
  {#if props.score ?? true}
    <span class="word-score">
      {#if props.word.swaps && isDaily}
        {props.word.swaps}{' '}<Redo />
      {:else if isClassic}
        +{props.word.score}
      {/if}
    </span>
  {/if}
</div>

<style lang="scss">
  @use '~/theme/include-media' as *;
  .static-word-container {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr [word] auto [score] 1fr;
    align-items: center;
    cursor: pointer;
  }
  .word-time {
    grid-column: 1;
    grid-row: 1;
    justify-self: start;
    font-size: 0.85em;
    font-style: italic;
    opacity: 0.5;
    white-space: nowrap;
  }
  .word {
    grid-column: word;
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 0.25em;
  }
  .word-score {
    grid-column: score;
    font-weight: bold;
    text-align: end;
    display: flex;
    justify-content: end;
    align-items: center;
    gap: 0.5em;
  }
</style>
